import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getCategories } from '../services/api';
import { useToast } from '../contexts/ToastContext';

function AddProductModal({ isOpen, onClose, onSubmit, loading: submitLoading, product: editProduct, isEditMode = false }) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const previousCategoryIdRef = useRef('');
  const { showError, showSuccess } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cost_price: '',
    price: '',
    original_price: '',
    percent_reduction: '',
    images: [], // Array of File objects or existing image URLs (strings)
    videos: [], // Array of File objects or existing video URLs (strings)
    stock: '',
    sold: '', // Sold quantity for non-color products
    category_id: '',
    subcategory_id: '',
    is_active: true,
    is_new: false,
    is_promotional: false,
  });

  // Separate state for existing images/videos (URLs) vs new uploads (Files)
  const [existingImages, setExistingImages] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  
  // Colors and sizes as arrays
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [newColor, setNewColor] = useState('');
  const [newSize, setNewSize] = useState('');
  
  // Color-image mapping: { "Black": [0, 1], "White": [2, 3] } - indices refer to allImages array
  const [colorImages, setColorImages] = useState({});
  
  // Color stock quantities: { "Black": 10, "White": 5 } - stock per color (used when no sizes)
  const [colorStock, setColorStock] = useState({});
  
  // Color sold quantities: { "Black": 5, "White": 3 } - sold per color (used when no sizes)
  const [colorSold, setColorSold] = useState({});
  
  // Variant stock quantities: { "White-Medium": 5, "White-Large": 10, "Pink-Medium": 3, "Pink-Large": 6 }
  const [variantStock, setVariantStock] = useState({});
  
  // Variant sold quantities: { "White-Medium": 2, "White-Large": 3, "Pink-Medium": 1, "Pink-Large": 2 }
  const [variantSold, setVariantSold] = useState({});
  
  // Helper to get all images (existing + new uploads)
  const allImages = useMemo(() => {
    return [...existingImages, ...formData.images.map((file, idx) => 
      file instanceof File ? URL.createObjectURL(file) : file
    )];
  }, [existingImages, formData.images]);

  // Fetch categories when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchCategories = async () => {
        try {
          setLoading(true);
          const data = await getCategories();
          setCategories(data);
        } catch (error) {
          console.error('Failed to fetch categories:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchCategories();
    }
  }, [isOpen]);

  // Load subcategories when category changes
  useEffect(() => {
    if (formData.category_id && categories.length > 0) {
      const selectedCategory = categories.find(cat => cat.id === parseInt(formData.category_id));
      if (selectedCategory && selectedCategory.subcategories) {
        setSubcategories(selectedCategory.subcategories);
      } else {
        setSubcategories([]);
      }
      // Reset subcategory only if category actually changed (not on initial load)
      if (previousCategoryIdRef.current && previousCategoryIdRef.current !== formData.category_id) {
        setFormData(prev => ({ ...prev, subcategory_id: '' }));
      }
      previousCategoryIdRef.current = formData.category_id;
    } else {
      setSubcategories([]);
      if (previousCategoryIdRef.current) {
        setFormData(prev => ({ ...prev, subcategory_id: '' }));
      }
      previousCategoryIdRef.current = '';
    }
  }, [formData.category_id, categories]);

  // Reset form when modal opens/closes or edit product changes
  useEffect(() => {
    if (!isOpen) return;
    
    if (isEditMode && editProduct) {
        // Handle existing images - could be array or single string
        const existingImgs = editProduct.images && Array.isArray(editProduct.images) 
          ? editProduct.images 
          : editProduct.image 
          ? [editProduct.image] 
          : [];
        
        // Handle existing videos - could be array or single string
        const existingVids = editProduct.videos && Array.isArray(editProduct.videos)
          ? editProduct.videos
          : editProduct.video
          ? [editProduct.video]
          : [];

        // Set existing media
        setExistingImages(existingImgs.filter(img => img && typeof img === 'string'));
        setExistingVideos(existingVids.filter(vid => vid && typeof vid === 'string'));
        
        // Populate form with existing product data for editing
        // Match exactly what the add modal saves
        console.log('Edit Product Data:', editProduct); // Debug log
        console.log('Cost Price Raw:', editProduct.cost_price, 'Type:', typeof editProduct.cost_price);
        console.log('Original Price Raw:', editProduct.original_price, 'Type:', typeof editProduct.original_price);
        console.log('Color Stock Raw:', editProduct.color_stock, 'Type:', typeof editProduct.color_stock);
        console.log('Color Sold Raw:', editProduct.color_sold, 'Type:', typeof editProduct.color_sold);
        
        // Helper to safely convert to string or empty
        const safeString = (val) => {
          if (val === null || val === undefined || val === '') return '';
          const numVal = parseFloat(val);
          return isNaN(numVal) ? '' : String(val);
        };
        
        // Ensure cost_price is always set - if missing, default to price or 0
        const costPrice = editProduct.cost_price !== null && editProduct.cost_price !== undefined 
          ? editProduct.cost_price 
          : (editProduct.price || 0);
        
        setFormData({
          name: editProduct.name || '',
          description: editProduct.description || '',
          cost_price: safeString(costPrice),
          price: safeString(editProduct.price),
          original_price: safeString(editProduct.original_price),
          percent_reduction: editProduct.original_price && editProduct.price && parseFloat(editProduct.original_price) > parseFloat(editProduct.price)
            ? String((((parseFloat(editProduct.original_price) - parseFloat(editProduct.price)) / parseFloat(editProduct.original_price)) * 100).toFixed(1))
            : '', // Calculate from original_price and price, not stored in DB
          images: [], // New file uploads only
          videos: [], // New file uploads only
          stock: editProduct.stock !== null && editProduct.stock !== undefined ? String(editProduct.stock) : '',
          sold: editProduct.sold !== null && editProduct.sold !== undefined ? String(editProduct.sold) : '',
          category_id: editProduct.category_id || editProduct.category?.id || '',
          subcategory_id: editProduct.subcategory_id || editProduct.subcategory?.id || '',
          is_active: editProduct.is_active !== false && editProduct.is_active !== null,
          is_new: editProduct.is_new === true || editProduct.is_new === 1 || editProduct.is_new === '1',
          is_promotional: editProduct.is_promotional === true || editProduct.is_promotional === 1 || editProduct.is_promotional === '1',
        });
        
        // Set colors and sizes
        setColors(editProduct.colors && Array.isArray(editProduct.colors) ? editProduct.colors : []);
        setSizes(editProduct.sizes && Array.isArray(editProduct.sizes) ? editProduct.sizes : []);
        
        // Initialize color stock and sold quantities
        // Get all colors from the product
        const productColors = editProduct.colors && Array.isArray(editProduct.colors) ? editProduct.colors : [];
        
        // Initialize color stock - ensure all colors are covered
        const loadedColorStock = {};
        productColors.forEach(color => {
          if (editProduct.color_stock && typeof editProduct.color_stock === 'object' && editProduct.color_stock[color] !== undefined) {
            const value = editProduct.color_stock[color];
            loadedColorStock[color] = (value === null || value === undefined || value === '') ? '' : String(value);
          } else {
            loadedColorStock[color] = '';
          }
        });
        setColorStock(loadedColorStock);
        console.log('📥 Loaded color_stock:', loadedColorStock);
        console.log('📥 Product colors:', productColors);
        console.log('📥 Raw color_stock from DB:', editProduct.color_stock);
        
        // Initialize color sold - ensure all colors are covered
        const loadedColorSold = {};
        productColors.forEach(color => {
          if (editProduct.color_sold && typeof editProduct.color_sold === 'object' && editProduct.color_sold[color] !== undefined) {
            const value = editProduct.color_sold[color];
            loadedColorSold[color] = (value === null || value === undefined || value === '') ? '' : String(value);
          } else {
            loadedColorSold[color] = '';
          }
        });
        setColorSold(loadedColorSold);
        console.log('📥 Loaded color_sold:', loadedColorSold);
        console.log('📥 Raw color_sold from DB:', editProduct.color_sold);
        
        // Initialize variant stock and sold quantities (for color-size combinations)
        const productSizes = editProduct.sizes && Array.isArray(editProduct.sizes) ? editProduct.sizes : [];
        
        if (productColors.length > 0 && productSizes.length > 0) {
          // Initialize variant stock
          const loadedVariantStock = {};
          productColors.forEach(color => {
            productSizes.forEach(size => {
              const variantKey = `${color}-${size}`;
              if (editProduct.variant_stock && typeof editProduct.variant_stock === 'object' && editProduct.variant_stock[variantKey] !== undefined) {
                const value = editProduct.variant_stock[variantKey];
                loadedVariantStock[variantKey] = (value === null || value === undefined || value === '') ? '' : String(value);
              } else {
                loadedVariantStock[variantKey] = '';
              }
            });
          });
          setVariantStock(loadedVariantStock);
          
          // Initialize variant sold
          const loadedVariantSold = {};
          productColors.forEach(color => {
            productSizes.forEach(size => {
              const variantKey = `${color}-${size}`;
              if (editProduct.variant_sold && typeof editProduct.variant_sold === 'object' && editProduct.variant_sold[variantKey] !== undefined) {
                const value = editProduct.variant_sold[variantKey];
                loadedVariantSold[variantKey] = (value === null || value === undefined || value === '') ? '' : String(value);
              } else {
                loadedVariantSold[variantKey] = '';
              }
            });
          });
          setVariantSold(loadedVariantSold);
        } else {
          setVariantStock({});
          setVariantSold({});
        }
        
        // Initialize color_images mapping
        // Convert color_images from backend format (URLs) to frontend format (indices)
        if (editProduct.color_images && typeof editProduct.color_images === 'object') {
          const colorImagesMap = {};
          const allExistingImgs = existingImgs.filter(img => img && typeof img === 'string');
          
          Object.keys(editProduct.color_images).forEach(color => {
            const imageUrls = editProduct.color_images[color];
            if (Array.isArray(imageUrls)) {
              // Map URLs to indices in existingImages array
              colorImagesMap[color] = imageUrls.map(url => {
                const index = allExistingImgs.indexOf(url);
                return index >= 0 ? index : url; // Use index if found, otherwise keep URL
              });
            }
          });
          setColorImages(colorImagesMap);
        } else {
          setColorImages({});
        }
        
        console.log('Form Data Set:', {
          cost_price: safeString(editProduct.cost_price),
          original_price: safeString(editProduct.original_price),
          price: safeString(editProduct.price)
        });
      } else {
        // Reset to empty form for new product
        setExistingImages([]);
        setExistingVideos([]);
        setColors([]);
        setSizes([]);
        setNewColor('');
        setNewSize('');
        setColorImages({});
        setColorStock({});
        setColorSold({});
        setVariantStock({});
        setVariantSold({});
        setFormData({
          name: '',
          description: '',
          cost_price: '',
          price: '',
          original_price: '',
          percent_reduction: '',
          images: [],
          videos: [],
          stock: '',
          category_id: '',
          subcategory_id: '',
          is_active: true,
          is_new: false,
          is_promotional: false,
        });
      }
  }, [isOpen, isEditMode, editProduct]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Image resizer helper function - resizes images to standard size for shop display
  const resizeImage = (file, targetWidth = 800, targetHeight = 1067, quality = 0.9) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate dimensions maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          const aspectRatio = width / height;
          
          // Target aspect ratio (3:4 for shop display)
          const targetAspectRatio = targetWidth / targetHeight;
          
          // Calculate new dimensions
          if (aspectRatio > targetAspectRatio) {
            // Image is wider - fit to width
            width = targetWidth;
            height = targetWidth / aspectRatio;
          } else {
            // Image is taller - fit to height
            height = targetHeight;
            width = targetHeight * aspectRatio;
          }
          
          // Ensure dimensions are integers
          width = Math.round(width);
          height = Math.round(height);
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Ensure high quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Determine output format - prefer JPEG for photos, PNG for transparency
          let outputType = file.type;
          if (!outputType || outputType === 'image/png') {
            // Check if image has transparency by examining alpha channel
            const imageData = ctx.getImageData(0, 0, width, height);
            const hasTransparency = Array.from(imageData.data).some((pixel, index) => {
              return (index + 1) % 4 === 0 && pixel < 255; // Alpha channel
            });
            outputType = hasTransparency ? 'image/png' : 'image/jpeg';
          } else if (outputType.startsWith('image/')) {
            outputType = 'image/jpeg';
          } else {
            outputType = 'image/jpeg';
          }
          
          // Convert to blob
          canvas.toBlob((blob) => {
            if (blob && blob.size > 0) {
              const originalName = file.name;
              const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
              const extension = originalName.substring(originalName.lastIndexOf('.')) || 
                               (outputType === 'image/png' ? '.png' : '.jpg');
              const newFileName = nameWithoutExt + extension;
              
              const resizedFile = new File([blob], newFileName, {
                type: outputType,
                lastModified: Date.now(),
              });
              
              console.log('Image resized:', {
                original: { name: file.name, size: file.size, dimensions: `${img.width}x${img.height}` },
                resized: { name: resizedFile.name, size: resizedFile.size, dimensions: `${width}x${height}` }
              });
              
              resolve(resizedFile);
            } else {
              console.warn('Resize failed, using original file');
              resolve(file);
            }
          }, outputType, quality);
        };
        img.onerror = (error) => {
          console.error('Image load error:', error);
          resolve(file);
        };
        img.src = e.target.result;
      };
      reader.onerror = (error) => {
        console.error('File read error:', error);
        resolve(file);
      };
      reader.readAsDataURL(file);
    });
  };

  // Image compression helper function
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
      
      // If file is already under the limit, skip compression
      if (file.size <= MAX_FILE_SIZE) {
        console.log(`File ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB) is within limit, skipping compression`);
        resolve(file);
        return;
      }
      
      // If file is very small, skip compression
      if (file.size < 2 * 1024 * 1024) { // Less than 2MB
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Ensure high quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Determine output format - prefer PNG for transparency, JPEG for photos
          let outputType = file.type;
          if (!outputType || outputType === 'image/png') {
            // Keep PNG format to preserve transparency
            outputType = 'image/png';
          } else if (outputType.startsWith('image/')) {
            // For other formats, use JPEG for better compression
            outputType = 'image/jpeg';
          } else {
            outputType = 'image/jpeg'; // Default fallback
          }
          
          // Convert to blob with compression
          canvas.toBlob((blob) => {
            if (blob && blob.size > 0) {
              // Ensure we preserve the original file extension
              const originalName = file.name;
              const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
              const extension = originalName.substring(originalName.lastIndexOf('.')) || 
                               (outputType === 'image/png' ? '.png' : '.jpg');
              const newFileName = nameWithoutExt + extension;
              
              // Create a new File object with proper MIME type
              const compressedFile = new File([blob], newFileName, {
                type: outputType,
                lastModified: Date.now(),
              });
              
              console.log('Image compressed:', {
                original: { name: file.name, size: file.size, type: file.type },
                compressed: { name: compressedFile.name, size: compressedFile.size, type: compressedFile.type }
              });
              
              resolve(compressedFile);
            } else {
              console.warn('Compression failed, using original file');
              resolve(file); // Fallback to original if compression fails
            }
          }, outputType, quality);
        };
        img.onerror = (error) => {
          console.error('Image load error:', error);
          resolve(file); // Fallback to original on error
        };
        img.src = e.target.result;
      };
      reader.onerror = (error) => {
        console.error('File read error:', error);
        resolve(file); // Fallback to original on error
      };
      reader.readAsDataURL(file);
    });
  };

  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      // Handle multiple file uploads - compress images before adding
      const newFiles = Array.from(files || []);
      const processedFiles = [];
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
      
      for (const file of newFiles) {
        // Check file size first
        if (file.size > MAX_FILE_SIZE) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          const maxSizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
          showError(
            `File "${file.name}" is too large (${fileSizeMB} MB). Maximum allowed size is ${maxSizeMB} MB. ` +
            `Please compress or resize the image before uploading.`
          );
          continue; // Skip this file
        }
        
        // Process image files - resize to standard size first, then compress if needed
        if (file.type.startsWith('image/')) {
          try {
            // Always resize images to standard size for consistent shop display
            console.log(`Resizing image: ${file.name}`);
            const resizedFile = await resizeImage(file, 800, 1067, 0.9);
            
            // After resizing, check if compression is still needed
            if (resizedFile.size <= MAX_FILE_SIZE) {
              console.log(`Adding resized image (within limit): ${resizedFile.name} (${(resizedFile.size / (1024 * 1024)).toFixed(2)} MB)`);
              processedFiles.push(resizedFile);
            } else {
              // Resized file still exceeds limit, try to compress further
              const fileSizeMB = (resizedFile.size / (1024 * 1024)).toFixed(2);
              console.log(`Resized file exceeds limit, compressing: ${resizedFile.name} (${fileSizeMB} MB)`);
              
              const compressedFile = await compressImage(resizedFile);
              
              // Check if compressed file is still too large
              if (compressedFile.size > MAX_FILE_SIZE) {
                const compressedSizeMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
                const maxSizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
                showError(
                  `File "${file.name}" is still too large after resizing and compression (${compressedSizeMB} MB). ` +
                  `Maximum allowed size is ${maxSizeMB} MB. Please use a smaller image or compress it further.`
                );
                continue; // Skip this file
              }
              
              // Validate compressed file before adding
              if (compressedFile instanceof File && compressedFile.size > 0) {
                // Ensure MIME type is valid
                if (compressedFile.type && compressedFile.type.startsWith('image/')) {
                  processedFiles.push(compressedFile);
                  
                  const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
                  const compressedSizeMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
                  const reduction = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
                  console.log(`✓ Resized and compressed ${file.name}: ${originalSizeMB} MB → ${compressedSizeMB} MB (${reduction}% reduction)`);
                } else {
                  console.warn('Compressed file has invalid MIME type, using resized file');
                  processedFiles.push(resizedFile);
                }
              } else {
                console.warn('Compressed file is invalid, using resized file');
                processedFiles.push(resizedFile);
              }
            }
          } catch (error) {
            console.error('Error processing image:', error);
            showError(`Failed to process "${file.name}". Please try a different image.`);
            continue; // Skip files that fail processing
          }
        } else {
          // Non-image files (like videos) are added as-is
          processedFiles.push(file);
        }
      }
      
      if (processedFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
          [name]: [...(prev[name] || []), ...processedFiles],
      }));
      }
      // Reset the input so the same file can be selected again if needed
      e.target.value = '';
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleAddColor = () => {
    if (newColor.trim() && !colors.includes(newColor.trim())) {
      const colorName = newColor.trim();
      setColors([...colors, colorName]);
      
      // Initialize stock and sold quantities for new color
      setColorStock(prev => ({
        ...prev,
        [colorName]: ''
      }));
      setColorSold(prev => ({
        ...prev,
        [colorName]: ''
      }));
      
      // If sizes exist, initialize variant stock for all color-size combinations
      if (sizes.length > 0) {
        const newVariantStock = { ...variantStock };
        const newVariantSold = { ...variantSold };
        sizes.forEach(size => {
          const variantKey = `${colorName}-${size}`;
          newVariantStock[variantKey] = '';
          newVariantSold[variantKey] = '';
        });
        setVariantStock(newVariantStock);
        setVariantSold(newVariantSold);
      }
      
      setNewColor('');
    }
  };

  const handleRemoveColor = (index) => {
    const colorToRemove = colors[index];
    setColors(colors.filter((_, i) => i !== index));
    
    // Remove color from colorImages mapping
    const newColorImages = { ...colorImages };
    delete newColorImages[colorToRemove];
    setColorImages(newColorImages);
    
    // Remove color from colorStock
    const newColorStock = { ...colorStock };
    delete newColorStock[colorToRemove];
    setColorStock(newColorStock);
    
    // Remove color from colorSold
    const newColorSold = { ...colorSold };
    delete newColorSold[colorToRemove];
    setColorSold(newColorSold);
    
    // Remove all variants with this color from variantStock and variantSold
    if (sizes.length > 0) {
      const newVariantStock = { ...variantStock };
      const newVariantSold = { ...variantSold };
      sizes.forEach(size => {
        const variantKey = `${colorToRemove}-${size}`;
        delete newVariantStock[variantKey];
        delete newVariantSold[variantKey];
      });
      setVariantStock(newVariantStock);
      setVariantSold(newVariantSold);
    }
  };

  const handleColorStockChange = (color, value) => {
    setColorStock(prev => ({
      ...prev,
      [color]: value
    }));
    // Don't auto-calculate total stock - keep color stocks separate
  };

  const handleColorSoldChange = (color, value) => {
    setColorSold(prev => ({
      ...prev,
      [color]: value
    }));
  };

  const handleAddSize = () => {
    if (newSize.trim() && !sizes.includes(newSize.trim())) {
      const sizeName = newSize.trim();
      setSizes([...sizes, sizeName]);
      
      // If colors exist, initialize variant stock for all color-size combinations
      if (colors.length > 0) {
        const newVariantStock = { ...variantStock };
        const newVariantSold = { ...variantSold };
        colors.forEach(color => {
          const variantKey = `${color}-${sizeName}`;
          newVariantStock[variantKey] = '';
          newVariantSold[variantKey] = '';
        });
        setVariantStock(newVariantStock);
        setVariantSold(newVariantSold);
      }
      
      setNewSize('');
    }
  };

  const handleRemoveSize = (index) => {
    const sizeToRemove = sizes[index];
    setSizes(sizes.filter((_, i) => i !== index));
    
    // Remove all variants with this size from variantStock and variantSold
    if (colors.length > 0) {
      const newVariantStock = { ...variantStock };
      const newVariantSold = { ...variantSold };
      colors.forEach(color => {
        const variantKey = `${color}-${sizeToRemove}`;
        delete newVariantStock[variantKey];
        delete newVariantSold[variantKey];
      });
      setVariantStock(newVariantStock);
      setVariantSold(newVariantSold);
    }
  };
  
  // Handler for variant stock changes
  const handleVariantStockChange = (color, size, value) => {
    const variantKey = `${color}-${size}`;
    setVariantStock(prev => ({
      ...prev,
      [variantKey]: value
    }));
  };
  
  // Handler for variant sold changes
  const handleVariantSoldChange = (color, size, value) => {
    const variantKey = `${color}-${size}`;
    setVariantSold(prev => ({
      ...prev,
      [variantKey]: value
    }));
  };

  const handleToggleImageForColor = (color, imageIndex) => {
    const newColorImages = { ...colorImages };
    if (!newColorImages[color]) {
      newColorImages[color] = [];
    }
    
    const imageIndexInArray = newColorImages[color].indexOf(imageIndex);
    if (imageIndexInArray >= 0) {
      // Remove image from color
      newColorImages[color] = newColorImages[color].filter(idx => idx !== imageIndex);
    } else {
      // Add image to color
      newColorImages[color] = [...newColorImages[color], imageIndex];
    }
    
    setColorImages(newColorImages);
  };

  const handleRemoveFile = (fileType, index) => {
    setFormData(prev => ({
      ...prev,
      [fileType]: prev[fileType].filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingVideo = (index) => {
    setExistingVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    const errors = [];

    // Name validation
    if (!formData.name || !formData.name.trim()) {
      errors.push('Product name is required');
    }

    // Price validation
    if (!formData.price || formData.price.trim() === '') {
      errors.push('Selling price is required');
    } else {
      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        errors.push('Selling price must be a valid number greater than or equal to 0');
      }
    }

    // Stock validation
    if (colors.length > 0 && sizes.length > 0) {
      // Validate variant stocks (color-size combinations)
      const hasValidStock = colors.some(color => 
        sizes.some(size => {
          const variantKey = `${color}-${size}`;
          const stock = variantStock[variantKey];
          return stock && parseInt(stock) > 0;
        })
      );
      if (!hasValidStock) {
        errors.push('At least one color-size combination must have a stock quantity greater than 0');
      }
      // Validate each variant stock is a valid number
      colors.forEach(color => {
        sizes.forEach(size => {
          const variantKey = `${color}-${size}`;
          const stock = variantStock[variantKey];
          if (stock && stock.toString().trim() !== '') {
            const stockNum = parseInt(stock);
            if (isNaN(stockNum) || stockNum < 0 || !Number.isInteger(stockNum)) {
              errors.push(`${color}-${size} stock quantity must be a valid whole number greater than or equal to 0`);
            }
          }
        });
      });
    } else if (colors.length > 0) {
      // Validate color stocks (when no sizes)
      const hasValidStock = colors.some(color => {
        const stock = colorStock[color];
        return stock && parseInt(stock) > 0;
      });
      if (!hasValidStock) {
        errors.push('At least one color must have a stock quantity greater than 0');
      }
      // Validate each color stock is a valid number
      colors.forEach(color => {
        const stock = colorStock[color];
        if (stock && stock.toString().trim() !== '') {
          const stockNum = parseInt(stock);
          if (isNaN(stockNum) || stockNum < 0 || !Number.isInteger(stockNum)) {
            errors.push(`${color} stock quantity must be a valid whole number greater than or equal to 0`);
          }
        }
      });
    } else {
      // Validate single stock field
      if (!formData.stock || formData.stock.toString().trim() === '') {
        errors.push('Stock quantity is required');
      } else {
        const stock = parseInt(formData.stock);
        if (isNaN(stock) || stock < 0 || !Number.isInteger(stock)) {
          errors.push('Stock quantity must be a valid whole number greater than or equal to 0');
        }
      }
    }

    // Cost price validation (required)
    const costPriceStrForValidation = String(formData.cost_price || '').trim();
    if (costPriceStrForValidation === '' || costPriceStrForValidation === 'undefined' || costPriceStrForValidation === 'null') {
      errors.push('Cost price is required');
    } else {
      const costPrice = parseFloat(costPriceStrForValidation);
      if (isNaN(costPrice) || costPrice < 0) {
        errors.push('Cost price must be a valid number greater than or equal to 0');
      }
    }

    // Original price validation (optional but must be valid if provided)
    if (formData.original_price && formData.original_price.trim() !== '') {
      const originalPrice = parseFloat(formData.original_price);
      if (isNaN(originalPrice) || originalPrice < 0) {
        errors.push('Original price must be a valid number greater than or equal to 0');
      }
      // If both original price and current price are provided, validate logic
      if (formData.price && formData.price.trim() !== '') {
        const currentPrice = parseFloat(formData.price);
        const origPrice = parseFloat(formData.original_price);
        if (!isNaN(currentPrice) && !isNaN(origPrice) && origPrice <= currentPrice) {
          errors.push('Original price must be greater than the current selling price for promotional pricing');
        }
      }
    }

    // Percent reduction validation (optional but must be valid if provided)
    if (formData.percent_reduction && formData.percent_reduction.trim() !== '') {
      const percentReduction = parseFloat(formData.percent_reduction);
      if (isNaN(percentReduction) || percentReduction < 0 || percentReduction > 100) {
        errors.push('Percent reduction must be a number between 0 and 100');
      }
    }

    // Images validation - must have at least one (existing or new)
    const totalImages = existingImages.length + (formData.images?.length || 0);
    if (totalImages === 0) {
      errors.push('At least one product image is required (either existing or new upload)');
    }

    // Category validation (optional)
    if (formData.category_id && formData.category_id !== '') {
      const categoryId = parseInt(formData.category_id);
      if (isNaN(categoryId) || categoryId < 1) {
        errors.push('Category must be a valid selection');
      }
    }

    // Show all validation errors
    if (errors.length > 0) {
      showError(errors.join('. '));
      return;
    }

    // Calculate percent reduction if both prices are provided
    let calculatedPercentReduction = null;
    if (formData.original_price && formData.price) {
      const oldPrice = parseFloat(formData.original_price);
      const currentPrice = parseFloat(formData.price);
      if (oldPrice > currentPrice && oldPrice > 0) {
        calculatedPercentReduction = ((oldPrice - currentPrice) / oldPrice) * 100;
      }
    }

    // Use provided percent_reduction or calculated value
    const percentReduction = formData.percent_reduction 
      ? parseFloat(formData.percent_reduction) 
      : calculatedPercentReduction;

    // Prepare data for submission
    // Only original_price and percent_reduction can be null (optional)
    // All other fields are required
    console.log('FormData before submission:', formData); // Debug
    console.log('Cost price from form:', formData.cost_price, 'Type:', typeof formData.cost_price);
    
    // Ensure cost_price is properly converted (already validated above, but double-check)
    const costPriceStr = String(formData.cost_price || '').trim();
    const costPriceNum = costPriceStr !== '' && costPriceStr !== 'undefined' && costPriceStr !== 'null' 
      ? parseFloat(costPriceStr) 
      : NaN;
    
    // Double-check validation (should have been caught above, but safety check)
    if (isNaN(costPriceNum) || !isFinite(costPriceNum) || costPriceNum < 0) {
      showError('Cost price must be a valid number greater than or equal to 0');
      return;
    }
    
    // Final validation: ensure cost_price is a valid number before submission
    if (typeof costPriceNum !== 'number' || isNaN(costPriceNum) || !isFinite(costPriceNum)) {
      showError('Cost price must be a valid number. Please check the form and try again.');
      return;
    }
    
    // Prepare color_images mapping - convert to format backend expects
    // Backend expects: { "Black": ["url1", "url2"], "White": ["url3"] }
    // We have: { "Black": [0, 1], "White": [2] } where numbers are indices in allImages
    // allImages = [existingImages..., newUploads...]
    // We need to map: existing image indices -> URLs, new upload indices -> need backend to map after upload
    const colorImagesForSubmit = {};
    if (colors.length > 0 && Object.keys(colorImages).length > 0) {
      colors.forEach(color => {
        if (colorImages[color] && colorImages[color].length > 0) {
          colorImagesForSubmit[color] = colorImages[color].map(index => {
            if (typeof index === 'number') {
              // Check if it's an existing image (in existingImages array) or a new upload
              if (index < existingImages.length) {
                // It's an existing image - return the URL
                return existingImages[index];
              } else {
                // It's a new upload - calculate relative index in formData.images array
                const relativeIndex = index - existingImages.length;
                // Send as a special marker that backend can resolve: "NEW_UPLOAD:{index}"
                // Backend will map this to the uploaded image URL
                return `NEW_UPLOAD:${relativeIndex}`;
              }
            } else {
              // It's already a URL string (from editing existing product)
              return index;
            }
          });
        }
      });
    }

    const submitData = {
      name: formData.name,
      description: formData.description || null,
      colors: colors.length > 0 ? colors : null,
      sizes: sizes.length > 0 ? sizes : null,
      color_images: Object.keys(colorImagesForSubmit).length > 0 ? colorImagesForSubmit : null,
      cost_price: costPriceNum, // Required - always sent (validated above, guaranteed to be a valid number)
      price: parseFloat(formData.price), // Required - always sent
      original_price: formData.original_price && formData.original_price.trim() !== '' ? parseFloat(formData.original_price) : null, // Optional
      percent_reduction: percentReduction, // Optional
      // When colors exist, save individual color stocks separately (don't sum)
      // Set main stock to 0 or sum for backward compatibility, but color_stock is the source of truth
      stock: colors.length > 0 
        ? 0 // Set to 0 when using color-specific stock
        : parseInt(formData.stock) || 0, // Required - always sent
      // For non-color products, allow manual sold quantity entry
      sold: colors.length > 0 
        ? 0 // Set to 0 when using color-specific sold
        : (formData.sold && formData.sold.trim() !== '') ? parseInt(formData.sold) || 0 : 0,
      // Convert color_stock and color_sold (used when colors exist but no sizes)
      color_stock: colors.length > 0 && sizes.length === 0 ? Object.keys(colorStock).reduce((acc, color) => {
        const value = colorStock[color];
        acc[color] = (value === '' || value === null || value === undefined) ? null : parseInt(value) || 0;
        return acc;
      }, {}) : null, // Stock per color if colors exist but no sizes (saved separately)
      color_sold: colors.length > 0 && sizes.length === 0 ? Object.keys(colorSold).reduce((acc, color) => {
        const value = colorSold[color];
        acc[color] = (value === '' || value === null || value === undefined) ? null : parseInt(value) || 0;
        return acc;
      }, {}) : null, // Sold per color if colors exist but no sizes (saved separately)
      // Convert variant_stock and variant_sold (used when both colors AND sizes exist)
      variant_stock: colors.length > 0 && sizes.length > 0 ? Object.keys(variantStock).reduce((acc, variantKey) => {
        const value = variantStock[variantKey];
        acc[variantKey] = (value === '' || value === null || value === undefined) ? null : parseInt(value) || 0;
        return acc;
      }, {}) : null, // Stock per color-size combination
      variant_sold: colors.length > 0 && sizes.length > 0 ? Object.keys(variantSold).reduce((acc, variantKey) => {
        const value = variantSold[variantKey];
        acc[variantKey] = (value === '' || value === null || value === undefined) ? null : parseInt(value) || 0;
        return acc;
      }, {}) : null, // Sold per color-size combination
      category_id: formData.category_id || null, // Required but can be null if no category selected
      subcategory_id: formData.subcategory_id || null, // Optional subcategory
      is_active: formData.is_active,
      is_new: formData.is_new,
      is_promotional: formData.is_promotional,
      images: formData.images && formData.images.length > 0 ? formData.images.filter(file => file instanceof File) : [], // New file uploads (only File objects)
      videos: formData.videos && formData.videos.length > 0 ? formData.videos.filter(file => file instanceof File) : [], // New file uploads (only File objects)
      existing_images: existingImages, // Existing image URLs to keep
      existing_videos: existingVideos, // Existing video URLs to keep
    };
    
    console.log('📤 Submitting product data:', submitData); // Debug log
    console.log('📤 Cost price in submitData:', submitData.cost_price, 'Type:', typeof submitData.cost_price, 'isNaN:', isNaN(submitData.cost_price), 'isFinite:', isFinite(submitData.cost_price));
    console.log('📤 All keys in submitData:', Object.keys(submitData));
    console.log('📤 color_stock:', submitData.color_stock);
    console.log('📤 color_sold:', submitData.color_sold);
    console.log('📤 variant_stock:', submitData.variant_stock);
    console.log('📤 variant_sold:', submitData.variant_sold);

    // Final validation: Check file sizes before submission
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
    const largeFiles = [];
    
    if (submitData.images && Array.isArray(submitData.images)) {
      submitData.images.forEach((file, index) => {
        if (file instanceof File && file.size > MAX_FILE_SIZE) {
          largeFiles.push({
            name: file.name,
            size: file.size,
            index: index + 1
          });
        }
      });
    }
    
    if (largeFiles.length > 0) {
      const fileList = largeFiles.map(f => {
        const sizeMB = (f.size / (1024 * 1024)).toFixed(2);
        return `${f.name} (${sizeMB} MB)`;
      }).join(', ');
      
      showError(
        `Cannot upload: One or more images are too large. Maximum file size is 100 MB.\n\n` +
        `Large files: ${fileList}\n\n` +
        `Please remove these files or compress them before uploading.`
      );
      return; // Stop submission
    }

    try {
      await onSubmit(submitData);
      // Only show success if onSubmit didn't throw an error
      showSuccess(isEditMode ? 'Product updated successfully!' : 'Product created successfully!');
      // Reset form for next product (only if not in edit mode)
      if (!isEditMode) {
        setFormData({
          name: '',
          description: '',
          cost_price: '',
          price: '',
          original_price: '',
          percent_reduction: '',
          stock: '',
          sold: '',
          category_id: '',
          is_active: true,
          is_new: false,
          is_promotional: false,
          images: [],
          videos: [],
        });
        setColors([]);
        setSizes([]);
        setColorImages({});
        setColorStock({});
        setColorSold({});
        setVariantStock({});
        setVariantSold({});
        setExistingImages([]);
        setExistingVideos([]);
      }
      // Don't close the modal - let user add more products or close manually
    } catch (err) {
      // Parse error message to show user-friendly messages
      let errorMessage = err.message || 'Failed to save product. Please try again.';
      
      // Check for file size errors
      if (errorMessage.includes('failed to upload') || errorMessage.includes('too large') || errorMessage.includes('max')) {
        errorMessage = 
          `Image upload failed. The file may be too large (maximum 100 MB) or in an unsupported format.\n\n` +
          `Please try:\n` +
          `• Compressing the image before uploading\n` +
          `• Using a smaller image file\n` +
          `• Ensuring the file is in JPG, PNG, GIF, WEBP, or HEIC format\n` +
          `• Checking your internet connection`;
      } else if (errorMessage.includes('422') || errorMessage.includes('Unprocessable')) {
        // Validation error - show more details
        errorMessage = 
          `Validation error: ${errorMessage}\n\n` +
          `Please check:\n` +
          `• All required fields are filled\n` +
          `• File sizes are within limits (100 MB max)\n` +
          `• File formats are supported (JPG, PNG, GIF, WEBP, HEIC)`;
      }
      
      showError(errorMessage);
      console.error('Product creation failed:', err);
      // Re-throw to prevent form reset
      throw err;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-2 md:p-3 lg:p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto"
      onClick={(e) => {
        // Only close if clicking the backdrop, not the modal content
        if (e.target === e.currentTarget && !submitLoading) {
          onClose();
        }
      }}
    >
      <div 
        className="relative bg-white rounded-none sm:rounded-lg md:rounded-xl lg:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl min-h-screen sm:min-h-0 max-h-screen sm:max-h-[96vh] md:max-h-[95vh] lg:max-h-[90vh] my-0 sm:my-auto overflow-hidden flex flex-col animate-modal-pop"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-primary via-primary-dark to-secondary px-3 sm:px-4 md:px-5 lg:px-6 py-2.5 sm:py-3 md:py-3.5 lg:py-4 xl:py-5 border-b border-white/20 flex-shrink-0 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-white drop-shadow-lg truncate">
                {isEditMode ? 'Edit Product' : 'Add New Product'}
              </h2>
              <p className="text-[10px] sm:text-xs md:text-sm text-white/90 mt-0.5 sm:mt-1 hidden sm:block">
                {isEditMode ? 'Update the product details' : 'Fill in the details to create a new product'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 md:p-2.5 text-white/90 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200 group flex-shrink-0"
              disabled={submitLoading}
              aria-label="Close modal"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 lg:p-5 xl:p-6 bg-gradient-to-br from-gray-50 to-white">
          <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-5">
            {/* Row 1: Name, Category, and Subcategory */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-5">
              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                  placeholder="Enter product name"
                  required
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Category
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium bg-white"
                >
                  <option value="">Select a category</option>
                  {loading ? (
                    <option disabled>Loading categories...</option>
                  ) : (
                    categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Subcategory
                </label>
                <select
                  name="subcategory_id"
                  value={formData.subcategory_id}
                  onChange={handleChange}
                  disabled={!formData.category_id || subcategories.length === 0}
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a subcategory</option>
                  {subcategories.length > 0 ? (
                    subcategories.map(subcategory => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No subcategories available</option>
                  )}
                </select>
              </div>
            </div>

            {/* Row 2: Description */}
            <div>
              <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium resize-none"
                placeholder="Enter product description"
              />
            </div>

            {/* Row 3: Cost Price, Current Price, Old Price, and Percent Reduction */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-5">
              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Cost Price (GHS) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="cost_price"
                  value={formData.cost_price || ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Selling Price (GHS) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Old Selling Price (GHS)
                </label>
                <input
                  type="number"
                  name="original_price"
                  value={formData.original_price || ''}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Percent Reduction (%)
                </label>
                <input
                  type="number"
                  name="percent_reduction"
                  value={formData.percent_reduction}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  max="100"
                  className="w-full px-3 md:px-4 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs md:text-sm font-medium"
                  placeholder="Auto-calculated"
                />
                {formData.original_price && formData.price && (
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1">
                    {(() => {
                      const oldPrice = parseFloat(formData.original_price);
                      const currentPrice = parseFloat(formData.price);
                      if (oldPrice > currentPrice && oldPrice > 0) {
                        const calc = ((oldPrice - currentPrice) / oldPrice) * 100;
                        return `Auto: ${calc.toFixed(1)}%`;
                      }
                      return '';
                    })()}
                  </p>
                )}
              </div>
            </div>

            {/* Row 4: Stock & Sold Quantity - Dynamic based on colors and sizes */}
            {colors.length > 0 && sizes.length > 0 ? (
              /* Color-Size Variant Matrix */
              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Stock & Sold Quantities per Color-Size Combination <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {colors.map((color) => {
                    return (
                      <div key={color} className="p-2 sm:p-3 md:p-4 bg-gradient-to-br from-blue-50 to-white rounded-lg md:rounded-xl border-2 border-blue-200">
                        <h4 className="text-xs sm:text-sm md:text-base font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2">
                          <div className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded-full bg-primary flex-shrink-0"></div>
                          {color}
                        </h4>
                        <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
                          {sizes.map((size) => {
                            const variantKey = `${color}-${size}`;
                            const stockValue = parseInt(variantStock[variantKey]) || 0;
                            const soldValue = parseInt(variantSold[variantKey]) || 0;
                            const remaining = stockValue - soldValue;
                            
                            return (
                              <div key={size} className="p-1.5 sm:p-2 md:p-3 bg-white rounded-lg border border-gray-200">
                                <p className="text-[10px] sm:text-xs font-semibold text-gray-700 mb-1.5 sm:mb-2">Size: {size}</p>
                                <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                                  <div>
                                    <label className="block text-[9px] sm:text-[10px] font-medium text-gray-600 mb-0.5 sm:mb-1">
                                      Stock <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="number"
                                      value={variantStock[variantKey] || ''}
                                      onChange={(e) => handleVariantStockChange(color, size, e.target.value)}
                                      min="0"
                                      className="w-full px-1.5 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs font-medium"
                                      placeholder="0"
                                      required
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[9px] sm:text-[10px] font-medium text-gray-600 mb-0.5 sm:mb-1">
                                      Sold
                                    </label>
                                    <input
                                      type="number"
                                      value={variantSold[variantKey] || ''}
                                      onChange={(e) => handleVariantSoldChange(color, size, e.target.value)}
                                      min="0"
                                      className="w-full px-1.5 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs font-medium"
                                      placeholder="0"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center justify-between pt-1 sm:pt-1.5 mt-1 sm:mt-1.5 border-t border-gray-200">
                                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-600">Remaining:</span>
                                  <span className={`text-[10px] sm:text-xs font-bold ${remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {remaining}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  <div className="mt-2 sm:mt-3 md:mt-4 pt-2 sm:pt-3 md:pt-4 border-t-2 border-gray-200 bg-gray-50 rounded-lg p-2 sm:p-3 md:p-4">
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 md:gap-4">
                      <div>
                        <span className="text-[10px] md:text-xs font-medium text-gray-600 block mb-1">Total Stock:</span>
                        <span className="text-base md:text-lg font-black text-primary">
                          {Object.values(variantStock).reduce((sum, stock) => {
                            const stockNum = parseInt(stock) || 0;
                            return sum + stockNum;
                          }, 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] md:text-xs font-medium text-gray-600 block mb-1">Total Sold:</span>
                        <span className="text-base md:text-lg font-black text-secondary">
                          {Object.values(variantSold).reduce((sum, sold) => {
                            const soldNum = parseInt(sold) || 0;
                            return sum + soldNum;
                          }, 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] md:text-xs font-medium text-gray-600 block mb-1">Total Remaining:</span>
                        <span className="text-base md:text-lg font-black text-green-600">
                          {Object.values(variantStock).reduce((sum, stock) => {
                            const stockNum = parseInt(stock) || 0;
                            return sum + stockNum;
                          }, 0) - Object.values(variantSold).reduce((sum, sold) => {
                            const soldNum = parseInt(sold) || 0;
                            return sum + soldNum;
                          }, 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : colors.length > 0 ? (
              /* Color-only (no sizes) */
              <div>
                <label className="block text-xs md:text-sm font-bold text-gray-700 mb-1.5 md:mb-2">
                  Stock & Sold Quantity by Color <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  {colors.map((color) => {
                    const stockValue = parseInt(colorStock[color]) || 0;
                    const soldValue = parseInt(colorSold[color]) || 0;
                    const remaining = stockValue - soldValue;
                    return (
                      <div key={color} className="p-2 sm:p-3 md:p-4 bg-gray-50 rounded-lg md:rounded-xl border-2 border-gray-200">
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 md:mb-3">
                          {color}
                        </label>
                        <div className="grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-3 mb-1.5 sm:mb-2 md:mb-3">
                          <div>
                            <label className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5 sm:mb-1">
                              Stock Quantity <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              value={colorStock[color] || ''}
                              onChange={(e) => handleColorStockChange(color, e.target.value)}
                              min="0"
                              className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs sm:text-sm font-medium"
                              placeholder="0"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5 sm:mb-1">
                              Sold Quantity
                            </label>
                            <input
                              type="number"
                              value={colorSold[color] || ''}
                              onChange={(e) => handleColorSoldChange(color, e.target.value)}
                              min="0"
                              className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs sm:text-sm font-medium"
                              placeholder="0"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-1.5 sm:pt-2 border-t border-gray-300">
                          <span className="text-[10px] sm:text-xs font-medium text-gray-600">Remaining:</span>
                          <span className={`text-xs sm:text-sm font-bold ${remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {remaining} available
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="mt-2 sm:mt-3 md:mt-4 pt-2 sm:pt-3 md:pt-4 border-t-2 border-gray-200 bg-gray-50 rounded-lg p-2 sm:p-3 md:p-4">
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 md:gap-4">
                      <div>
                        <span className="text-[10px] md:text-xs font-medium text-gray-600 block mb-1">Total Stock:</span>
                        <span className="text-base md:text-lg font-black text-primary">
                          {Object.values(colorStock).reduce((sum, stock) => {
                            const stockNum = parseInt(stock) || 0;
                            return sum + stockNum;
                          }, 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] md:text-xs font-medium text-gray-600 block mb-1">Total Sold:</span>
                        <span className="text-base md:text-lg font-black text-secondary">
                          {Object.values(colorSold).reduce((sum, sold) => {
                            const soldNum = parseInt(sold) || 0;
                            return sum + soldNum;
                          }, 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] md:text-xs font-medium text-gray-600 block mb-1">Total Remaining:</span>
                        <span className="text-base md:text-lg font-black text-green-600">
                          {Object.values(colorStock).reduce((sum, stock) => {
                            const stockNum = parseInt(stock) || 0;
                            return sum + stockNum;
                          }, 0) - Object.values(colorSold).reduce((sum, sold) => {
                            const soldNum = parseInt(sold) || 0;
                            return sum + soldNum;
                          }, 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 sm:mb-2">
                  Stock & Sold Quantity <span className="text-red-500">*</span>
                </label>
                <div className="p-2 sm:p-3 md:p-4 bg-gray-50 rounded-lg md:rounded-xl border-2 border-gray-200">
                  <div className="grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-3 mb-1.5 sm:mb-2 md:mb-3">
                    <div>
                      <label className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5 sm:mb-1">
                        Stock Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="stock"
                        value={formData.stock}
                        onChange={handleChange}
                        min="0"
                        className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs sm:text-sm font-medium"
                        placeholder="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5 sm:mb-1">
                        Sold Quantity
                      </label>
                      <input
                        type="number"
                        name="sold"
                        value={formData.sold || ''}
                        onChange={handleChange}
                        min="0"
                        className="w-full px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg md:rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 text-xs sm:text-sm font-medium"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1.5 sm:pt-2 border-t border-gray-300">
                    <span className="text-[10px] sm:text-xs font-medium text-gray-600">Remaining:</span>
                    <span className={`text-xs sm:text-sm font-bold ${
                      ((parseInt(formData.stock) || 0) - (parseInt(formData.sold) || 0)) > 0 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {Math.max(0, (parseInt(formData.stock) || 0) - (parseInt(formData.sold) || 0))} available
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Row 5: Colors */}
            <div>
              <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 sm:mb-2">
                Colors (Optional)
              </label>
              <div className="flex flex-col sm:flex-row gap-2 mb-2">
                <input
                  type="text"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddColor())}
                  className="flex-1 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs sm:text-sm"
                  placeholder="e.g., 4 Pieces Black"
                />
                <button
                  type="button"
                  onClick={handleAddColor}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all text-xs sm:text-sm font-semibold whitespace-nowrap"
                >
                  Add
                </button>
              </div>
              {colors.length > 0 && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {colors.map((color, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium"
                      >
                        {color}
                        <button
                          type="button"
                          onClick={() => handleRemoveColor(index)}
                          className="hover:text-red-600 transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  
                  {/* Image-Color Association */}
                  {allImages.length > 0 && (
                    <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
                      <p className="text-[10px] sm:text-xs font-semibold text-gray-600 mb-1.5 sm:mb-2">Associate images with colors:</p>
                      <div className="space-y-2 sm:space-y-3">
                        {colors.map((color) => (
                          <div key={color} className="space-y-1">
                            <label className="text-[10px] sm:text-xs font-medium text-gray-700">{color}:</label>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                              {allImages.map((imgUrl, imgIndex) => {
                                const isSelected = colorImages[color]?.includes(imgIndex);
                                return (
                                  <button
                                    key={imgIndex}
                                    type="button"
                                    onClick={() => handleToggleImageForColor(color, imgIndex)}
                                    className={`relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                      isSelected
                                        ? 'border-primary ring-2 ring-primary/50'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <img
                                      src={imgUrl}
                                      alt={`${imgIndex + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                    {isSelected && (
                                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                        <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Row 6: Sizes */}
            <div>
              <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 sm:mb-2">
                Sizes (Optional)
              </label>
              <div className="flex flex-col sm:flex-row gap-2 mb-2">
                <input
                  type="text"
                  value={newSize}
                  onChange={(e) => setNewSize(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSize())}
                  className="flex-1 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-xs sm:text-sm"
                  placeholder="e.g., S, M, L, XL"
                />
                <button
                  type="button"
                  onClick={handleAddSize}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all text-xs sm:text-sm font-semibold whitespace-nowrap"
                >
                  Add
                </button>
              </div>
              {sizes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {sizes.map((size, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium"
                    >
                      {size}
                      <button
                        type="button"
                        onClick={() => handleRemoveSize(index)}
                        className="hover:text-red-600 transition-colors"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Row 7: Image Uploads */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="flex-1 min-w-0">
                  <label className="block text-xs sm:text-sm font-bold text-gray-700">
                    Product Images {((existingImages.length + formData.images.length) > 0) && <span className="text-gray-500 font-normal text-[10px] sm:text-xs">({existingImages.length} existing, {formData.images.length} new)</span>}
                  </label>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                    Images will be automatically resized to 800x1067px (3:4 ratio) for optimal shop display
                  </p>
                </div>
                <input
                  type="file"
                  name="images"
                  onChange={handleChange}
                  accept="image/*,.heic,.heif"
                  multiple
                  className="hidden"
                  id="images-upload"
                />
                <label
                  htmlFor="images-upload"
                  className="flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 text-[10px] sm:text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm whitespace-nowrap flex-shrink-0"
                >
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Images</span>
                </label>
              </div>
              
              {/* Display existing images (from product) */}
              {existingImages.length > 0 && (
                <div className="mb-3 sm:mb-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Existing Images:</p>
                  <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                    {existingImages.map((imageUrl, index) => (
                      <div key={`existing-${index}`} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden border-2 border-blue-300 bg-blue-50">
                          <img
                            src={imageUrl}
                            alt={`Existing ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/200?text=Image+Error';
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingImage(index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <p className="text-xs text-gray-500 mt-1 truncate">Existing</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Display new uploaded images */}
              {formData.images.length > 0 && (
                <div className={existingImages.length > 0 ? 'mt-3 sm:mt-4' : ''}>
                  {existingImages.length > 0 && <p className="text-xs font-semibold text-gray-600 mb-2">New Images:</p>}
                  <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                    {formData.images.map((file, index) => (
                      <div key={`new-${index}`} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile('images', index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <p className="text-xs text-gray-500 mt-1 truncate">{file.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Empty state - only show if no existing or new images */}
              {existingImages.length === 0 && formData.images.length === 0 && (
                <div className="border-2 border-dashed border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 text-center">
                  <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-400">No images uploaded yet</p>
                </div>
              )}
            </div>

            {/* Row 6: Video Uploads */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                <label className="block text-xs sm:text-sm font-bold text-gray-700">
                  Product Videos {((existingVideos.length + formData.videos.length) > 0) && <span className="text-gray-500 font-normal text-[10px] sm:text-xs">({existingVideos.length} existing, {formData.videos.length} new)</span>}
                </label>
                <input
                  type="file"
                  name="videos"
                  onChange={handleChange}
                  accept="video/*"
                  multiple
                  className="hidden"
                  id="videos-upload"
                />
                <label
                  htmlFor="videos-upload"
                  className="flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 text-[10px] sm:text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm whitespace-nowrap flex-shrink-0"
                >
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Videos</span>
                </label>
              </div>
              
              {/* Display existing videos (from product) */}
              {existingVideos.length > 0 && (
                <div className="mb-3 sm:mb-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Existing Videos:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                    {existingVideos.map((videoUrl, index) => (
                      <div key={`existing-video-${index}`} className="relative group border-2 border-blue-300 rounded-lg p-2 bg-blue-50">
                        <div className="flex items-center gap-2">
                          <svg className="w-8 h-8 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">Existing Video</p>
                            <p className="text-xs text-gray-500 truncate">{videoUrl}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveExistingVideo(index)}
                            className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600 flex-shrink-0"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Display new uploaded videos */}
              {formData.videos.length > 0 && (
                <div className={existingVideos.length > 0 ? 'mt-3 sm:mt-4' : ''}>
                  {existingVideos.length > 0 && <p className="text-xs font-semibold text-gray-600 mb-2">New Videos:</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                    {formData.videos.map((file, index) => (
                      <div key={`new-video-${index}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 group">
                        <svg className="w-8 h-8 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile('videos', index)}
                          className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Empty state - only show if no existing or new videos */}
              {existingVideos.length === 0 && formData.videos.length === 0 && (
                <div className="border-2 border-dashed border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 text-center">
                  <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs sm:text-sm text-gray-400">No videos uploaded yet</p>
                </div>
              )}
            </div>

            {/* Row 6: Checkboxes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-4 pt-2 sm:pt-3">
              <label className="flex items-center gap-1.5 sm:gap-2 md:gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded border-2 border-gray-300 text-cyan-600 focus:ring-2 focus:ring-cyan-500/20 focus:ring-offset-2 cursor-pointer flex-shrink-0"
                />
                <span className="text-[11px] sm:text-xs md:text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  Active Product
                </span>
              </label>

              <label className="flex items-center gap-1.5 sm:gap-2 md:gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="is_new"
                  checked={formData.is_new}
                  onChange={handleChange}
                  className="w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded border-2 border-gray-300 text-cyan-600 focus:ring-2 focus:ring-cyan-500/20 focus:ring-offset-2 cursor-pointer flex-shrink-0"
                />
                <span className="text-[11px] sm:text-xs md:text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  New Product Badge
                </span>
              </label>

              <label className="flex items-center gap-1.5 sm:gap-2 md:gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="is_promotional"
                  checked={formData.is_promotional}
                  onChange={handleChange}
                  className="w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5 rounded border-2 border-gray-300 text-cyan-600 focus:ring-2 focus:ring-cyan-500/20 focus:ring-offset-2 cursor-pointer flex-shrink-0"
                />
                <span className="text-[11px] sm:text-xs md:text-sm font-medium text-gray-700 group-hover:text-gray-900">
                  Promotional Badge
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 md:gap-3 lg:gap-4 pt-3 md:pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={submitLoading}
                className="w-full sm:w-auto px-3 md:px-4 lg:px-6 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl border-2 border-gray-300 text-gray-700 text-xs md:text-sm lg:text-base font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitLoading}
                className="w-full sm:w-auto px-3 md:px-4 lg:px-6 py-2 md:py-2.5 lg:py-3 rounded-lg md:rounded-xl bg-gradient-to-r from-primary via-primary-dark to-secondary text-white text-xs md:text-sm lg:text-base font-semibold hover:shadow-xl hover:shadow-primary/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {isEditMode ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      )}
                    </svg>
                    <span>{isEditMode ? 'Update Product' : 'Create Product'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddProductModal;

